import { useState } from 'react'
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import { ChevronDown, LayoutDashboard, LogIn, LogOut, Menu, Moon, PackageCheck, Settings, ShoppingBag, Store, Sun, UserRound, UsersRound, X } from 'lucide-react'
import { useTheme } from '../hooks/useTheme'
import { useCart } from '../contexts/cartStore'
import { usePendingOrdersCount } from '../hooks/usePendingOrdersCount'

const navClass = ({ isActive }: { isActive: boolean }) =>
  `nav-link ${isActive ? 'nav-link-active' : ''}`

const Navbar = () => {
  const { user, logout } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const { theme, toggleTheme } = useTheme()
  const { itemCount } = useCart()
  const pendingOrders = usePendingOrdersCount(Boolean(user?.isAdmin), location.pathname)
  const orderBadge = pendingOrders > 0 ? <span className="notification-badge">{pendingOrders > 99 ? '99+' : pendingOrders}</span> : null
  const showCart = location.pathname.startsWith('/boutique') || location.pathname === '/panier' || itemCount > 0

  const handleLogout = () => {
    logout()
    setIsMenuOpen(false)
    navigate('/')
  }

  const closeMenu = () => setIsMenuOpen(false)

  return (
    <header className="sticky top-0 z-40 px-3 pt-3 sm:px-5 sm:pt-4">
      <nav className="nav-shell mx-auto max-w-[1400px]" aria-label="Navigation principale">
        <Link to="/" className="brand-mark" onClick={closeMenu} aria-label="Taï-Jitsu Club — Accueil">
          <img className="brand-logo" src="/logo-taijitsu.jpeg" alt="" />
          <span>
            <strong>Taï-Jitsu</strong>
            <small>Club Drômois</small>
          </span>
        </Link>

        <div className="hidden items-center gap-1 lg:flex">
          <NavLink to="/" className={navClass}>Accueil</NavLink>
          <NavLink to="/club" className={navClass}>Découvrir</NavLink>
          <NavLink to="/boutique" className={navClass}>Boutique</NavLink>
          <NavLink to="/bureau" className={navClass}>Le bureau</NavLink>
          {user && (
            <>
              <NavLink to="/events" className={navClass}>Événements</NavLink>
              <NavLink to="/members" className={navClass}>Adhérents</NavLink>
              <NavLink to="/techniques" className={navClass}>Techniques</NavLink>
              {user.isAdmin && <NavLink to="/admin" className={navClass}>Administration {orderBadge}</NavLink>}
            </>
          )}
          {!user && <NavLink to="/contact" className={navClass}>Cours d'essai ?</NavLink>}
        </div>

        <div className="hidden items-center gap-3 lg:flex">
          {showCart && (
            <Link to="/panier" className="nav-cart-link" aria-label={`Panier, ${itemCount} article${itemCount > 1 ? 's' : ''}`} title="Panier">
              <ShoppingBag />
              {itemCount > 0 && <span>{itemCount > 99 ? '99+' : itemCount}</span>}
            </Link>
          )}
          <button type="button" className="theme-toggle" onClick={toggleTheme} aria-label={theme === 'dark' ? 'Activer le mode clair' : 'Activer le mode sombre'} title={theme === 'dark' ? 'Mode clair' : 'Mode sombre'}>
            {theme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </button>
          {user ? (
            <div className="group relative">
              <button className="user-chip" type="button">
                <span className="user-avatar">{user.firstName.charAt(0)}{user.lastName.charAt(0)}</span>
                <span className="max-w-36 truncate">{user.firstName} {user.lastName}</span>
                {user.isAdmin && orderBadge}
                <ChevronDown className="h-4 w-4 transition-transform group-hover:rotate-180" />
              </button>
              <div className="nav-dropdown invisible absolute right-0 top-full mt-2 w-60 translate-y-2 opacity-0 transition-all group-focus-within:visible group-focus-within:translate-y-0 group-focus-within:opacity-100 group-hover:visible group-hover:translate-y-0 group-hover:opacity-100">
                <Link to="/settings"><Settings className="h-4 w-4" /> Mes paramètres</Link>
                <Link to="/mes-commandes"><PackageCheck className="h-4 w-4" /> Mes commandes</Link>
                {user.isAdmin && <Link to="/admin?section=shop-orders"><LayoutDashboard className="h-4 w-4" /> Commandes à traiter {orderBadge}</Link>}
                <button onClick={handleLogout}><LogOut className="h-4 w-4" /> Déconnexion</button>
              </div>
            </div>
          ) : (
            <Link to="/login" className="btn-primary"><LogIn className="h-4 w-4" /> Espace membre</Link>
          )}
        </div>

        <button
          type="button"
          className="grid h-11 w-11 place-items-center rounded-2xl text-emerald-950 transition hover:bg-emerald-50 lg:hidden"
          onClick={() => setIsMenuOpen((open) => !open)}
          aria-expanded={isMenuOpen}
          aria-label={isMenuOpen ? 'Fermer le menu' : 'Ouvrir le menu'}
        >
          {isMenuOpen ? <X /> : <Menu />}
        </button>

        {isMenuOpen && (
          <div className="mobile-menu absolute left-3 right-3 top-[calc(100%+8px)] p-3 lg:hidden">
            <button type="button" className="nav-link w-full" onClick={toggleTheme}>
              {theme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
              {theme === 'dark' ? 'Mode clair' : 'Mode sombre'}
            </button>
            <NavLink to="/" className={navClass} onClick={closeMenu}>Accueil</NavLink>
            <NavLink to="/club" className={navClass} onClick={closeMenu}>Découvrir le club</NavLink>
            <NavLink to="/boutique" className={navClass} onClick={closeMenu}><Store className="h-4 w-4" /> Boutique</NavLink>
            <NavLink to="/bureau" className={navClass} onClick={closeMenu}><UsersRound className="h-4 w-4" /> Le bureau</NavLink>
            {showCart && <NavLink to="/panier" className={navClass} onClick={closeMenu}><ShoppingBag className="h-4 w-4" /> Panier {itemCount > 0 ? `(${itemCount})` : ''}</NavLink>}
            {user ? (
              <>
                <NavLink to="/events" className={navClass} onClick={closeMenu}>Événements</NavLink>
                <NavLink to="/members" className={navClass} onClick={closeMenu}>Adhérents</NavLink>
                <NavLink to="/techniques" className={navClass} onClick={closeMenu}>Techniques</NavLink>
                <NavLink to="/settings" className={navClass} onClick={closeMenu}>Mes paramètres</NavLink>
                <NavLink to="/mes-commandes" className={navClass} onClick={closeMenu}>Mes commandes</NavLink>
                {user.isAdmin && <NavLink to="/admin" className={navClass} onClick={closeMenu}>Administration {orderBadge}</NavLink>}
                <button className="nav-link w-full" onClick={handleLogout}><LogOut className="h-4 w-4" /> Déconnexion</button>
              </>
            ) : (
              <>
                <NavLink to="/contact" className={navClass} onClick={closeMenu}>Cours d'essai ?</NavLink>
                <NavLink to="/login" className="btn-primary mt-2 justify-center" onClick={closeMenu}><UserRound className="h-4 w-4" /> Espace membre</NavLink>
              </>
            )}
          </div>
        )}
      </nav>
    </header>
  )
}

export default Navbar
